import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import {
  DateFilterTypesEnum,
  EntityType,
  PaidFlowTypesEnum,
  PaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentMethodVM,
  PaymentService
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import {
  BulkPayAmount,
  BulkPayRequest,
  collectSettlePayGroupIds,
  mapPayResponseToAction,
  payPaymentDueAccountGroup,
} from "../../services/paymentDueAccount.service";
import { UserLoginData } from "../../components/common/charts/Enums";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import './payment.css';
import PayableCard from "../listings/listing-cards/payables";
import PaymentPayablesORReceivablesList from "./PaymentPayablesORReceivablesList";
import PaymentSummery from "./PaymentSummery";
import Card from "../card";
import SimpleSpinner from "../../components/loader";
import { useCancelNavigation } from "../../utils/useCancelNavigation";
import { buildPaymentDateFilterQueryArgs, filterPayableDues, getPaymentListingPath, loadPayableHeader } from "./paymentListHelpers";
import {
  usePaymentSubmitGuard,
  NextIdempotencyKey,
} from "../../hooks/usePaymentSubmitGuard";
import { isPlaidCapablePayeeType } from "./payeePlaidHelpers";

interface ApiData {
  paymentPayeeList: PaymentDueAccountVM[];
  paymentDetail: PaymentDueAccountVM | null;
  paymentMethod: PaymentMethodVM[];
}

interface PaymentSelectionState {
  selectedPayments: PaymentDueAccountVM[];
  totalAmount: number;
  /** Partial overrides for PUT /pay/group; omit when empty. */
  amounts?: BulkPayAmount[];
}

interface PaymentFormData {
  PaymentModeId: string;
  paymentMethodTypeId: string;
  checkNum: string;
  transactionId: string;
  assistanceProvider: string;
  date: string;
}

const INITIAL_API_DATA: ApiData = {
  paymentMethod: [],
  paymentPayeeList: [],
  paymentDetail: null
};

const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
};

// Payment recording modes -- mirrors PaymentMethodTypesEnum on the backend.
const PAYMENT_MODE = PaymentMethodTypesEnum;

const PayablesPostPayment = () => {
  const [data, setData] = useState<ApiData>(INITIAL_API_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const { isSubmitting: isPaySubmitting, runGuarded: runPayGuarded } =
    usePaymentSubmitGuard();
  const [paymentState, setPaymentState] = useState<PaymentSelectionState>({
    selectedPayments: [],
    totalAmount: 0,
  });
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState("paidFlowTypeId == 1");
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  // Same entity resolution as EditPayable — read from URL search params
  const isEntity = searchParams.get('isEntity') === 'true';
  const entityId = Number(searchParams.get('entityId')) || undefined;
  const entityTypeId = Number(searchParams.get('entityTypeId')) || undefined;
  const payeeAccountId = searchParams.get('pai');

  const listingPath = useMemo(
    () =>
      getPaymentListingPath({
        isEntity,
        entityId,
        entityTypeId,
        paidFlowTypeId: PaidFlowTypesEnum.Payable,
      }),
    [isEntity, entityId, entityTypeId],
  );

  const fetchPayableHeader = useCallback(
    () => loadPayableHeader(isEntity, entityTypeId, entityId, payeeAccountId, id),
    [id, isEntity, entityTypeId, entityId, payeeAccountId],
  );

  // Does not catch its own errors: refreshAfterDelete needs to tell "fetch failed" apart from
  // "fetched fine, there's genuinely nothing left" (a caught-and-swallowed null would collapse
  // both into the same outcome and wrongly navigate the user away on a transient failure).
  const fetchInitialData = useCallback(async (): Promise<PaymentDueAccountVM | null> => {
    setIsLoading(true);
    try {
      const [paymentMethod, detail] = await Promise.all([
        apiHelper(
          PaymentService.getApiPaymentMethodOrganizationlist,
          { showNotification: false },
          organizationId
        ),
        fetchPayableHeader(),
      ]);

      setData(prev => ({
        ...prev,
        paymentMethod: paymentMethod?.data?.success || [],
        paymentDetail: detail,
      }));
      return detail;
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, fetchPayableHeader]);


  const fetchPaymentPayeeList = useCallback(async () => {
    try {
      let res;
      const dateFilterArgs = buildPaymentDateFilterQueryArgs(dateFilterType);

      if (isEntity && entityTypeId === EntityType.Property && payeeAccountId && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountPropertyList,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      } else if (isEntity && entityTypeId === EntityType.Unit && payeeAccountId && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountUnitList,
          { showNotification: false },
          Number(payeeAccountId),
          Number(entityId),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      } else if (isEntity && entityTypeId === EntityType.Tenant && entityId) {
        res = await apiHelper(
          PaymentService.getApiInvoicePayeeAccountTenantList,
          { showNotification: false },
          Number(entityId),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      } else {
        res = await apiHelper(
          PaymentService.getApiPaymentDueAccountInvoicelist,
          { showNotification: false },
          id,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          ...dateFilterArgs,
        );
      }

      setData(prev => ({
        ...prev,
        paymentPayeeList: filterPayableDues<PaymentDueAccountVM>(res?.data?.success || [])
      }));

      return res;
    } catch (error) {
      // Does not rethrow: this already shows its own accurate toast, and refreshAfterDelete's
      // Promise.all only branches on fetchInitialData's result -- rethrowing here would also
      // trip refreshAfterDelete's catch, double-toasting a single failure.
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment payee list",
        type: NotificationType.Failure,
      });
    }
  }, [isEntity, entityTypeId, payeeAccountId, entityId, dateFilterType, fullyQualifiedFilter, id]);


  useEffect(() => {
    fetchInitialData().catch(() => {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    });
  }, [fetchInitialData]);

  useEffect(() => {
    fetchPaymentPayeeList();
  }, [fetchPaymentPayeeList]);

  const refreshAfterDelete = useCallback(async () => {
    setPaymentState({ selectedPayments: [], totalAmount: 0, amounts: undefined });
    try {
      const [detail] = await Promise.all([fetchInitialData(), fetchPaymentPayeeList()]);
      if (!detail) {
        navigate(listingPath, { replace: true });
      }
    } catch {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to refresh payments after delete",
        type: NotificationType.Failure,
      });
    }
  }, [fetchInitialData, fetchPaymentPayeeList, navigate, listingPath]);

  const handleDeleteSuccess = useCallback(() => {
    navigate(listingPath, { replace: true });
  }, [navigate, listingPath]);

  const handleSetSelectedPayments = useCallback((
    payments: PaymentDueAccountVM[],
    totalAmount: number,
    amounts?: BulkPayAmount[],
  ) => {
    setPaymentState({
      selectedPayments: payments,
      totalAmount,
      amounts,
    });
  }, []);

  const methods = useForm<PaymentFormData>({
    mode: "onChange",
    defaultValues: {
      PaymentModeId: "",
      paymentMethodTypeId: "",
      checkNum: "",
      transactionId: "",
      assistanceProvider: "",
      date: new Date().toISOString().split('T')[0]
    }
  });

  const onNavigate = useCancelNavigation(listingPath);

  const selectedPaymentMethodTypeId = methods.watch("paymentMethodTypeId");
  const submitButtonLabel = Number(selectedPaymentMethodTypeId) === PAYMENT_MODE.Plaid ? "Pay" : "Save";

  // Hide Plaid when the payable's payee type cannot receive an online transfer (Bank Account,
  // Vendor, Utility, …). Prefer the header detail; fall back to the first selected due.
  const showPlaid = useMemo(
    () =>
      isPlaidCapablePayeeType(
        data.paymentDetail?.payeeAccountTypeId ??
          paymentState.selectedPayments[0]?.payeeAccountTypeId,
      ),
    [data.paymentDetail?.payeeAccountTypeId, paymentState.selectedPayments],
  );

  // If Plaid was already selected and the payee type is not Plaid-capable, fall back to Cash
  // so we never submit PaymentMethodTypeId=Plaid for BankAccount/Vendor/etc.
  useEffect(() => {
    if (showPlaid) return;
    if (Number(selectedPaymentMethodTypeId) !== PAYMENT_MODE.Plaid) return;
    methods.setValue("paymentMethodTypeId", String(PAYMENT_MODE.Cash), {
      shouldDirty: true,
      shouldValidate: true,
    });
    methods.setValue("PaymentModeId", "", { shouldValidate: true });
  }, [showPlaid, selectedPaymentMethodTypeId, methods]);

  const submitPayments = async (
    formData: PaymentFormData,
    nextKey: NextIdempotencyKey,
  ) => {



    if (paymentState.selectedPayments.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one payment",
        type: NotificationType.Warning,
      });
      return;
    }



    const paymentMethodTypeId = Number(formData?.paymentMethodTypeId);

    // Full rows expand every grouped id; Partial rows send only ids that received amounts.
    const paymentDueAccountIds = collectSettlePayGroupIds(
      paymentState.selectedPayments,
      {},
      paymentState.amounts,
    );

    if (paymentDueAccountIds.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one payment",
        type: NotificationType.Warning,
      });
      return;
    }

    const payload: BulkPayRequest = {
      paymentDueAccountIds,
      paymentMethodTypeId,
      paymentDate: new Date(formData.date).toISOString(),
      idempotencyKey: nextKey(),
    };

    if (paymentState.amounts?.length) {
      payload.amounts = paymentState.amounts;
    }

    // Only send the field the selected recording mode requires.
    if (paymentMethodTypeId === PAYMENT_MODE.Plaid) {
      payload.fromPaymentMethodId = Number(formData?.PaymentModeId);
    } else if (paymentMethodTypeId === PAYMENT_MODE.Check) {
      payload.checkNumber = formData?.checkNum;
    } else if (paymentMethodTypeId === PAYMENT_MODE.Other) {
      payload.transactionId = formData?.transactionId;
    }

    const response = await apiHelper(
      payPaymentDueAccountGroup,
      { showNotification: false },
      payload,
    );

    const action = mapPayResponseToAction(response);

    // Plaid/Default reserved at least one due for transfer -- the request itself was still
    // accepted, so this is treated the same as a fully-paid submission: an immediate, honest
    // toast (action.message already reflects the pending-confirmation wording -- see
    // getPaymentPayOutcome) and navigate away right away. The due's own pending/failed state is
    // then visible via PaymentPayablesORReceivablesList.tsx's persistent StatusTag badge once
    // the user lands back on the list -- no on-page blocking/polling needed here.
    PubSub.publish(TopicsEnum.Notification.toString(), {
      message:
        action.message ??
        (action.status === "success"
          ? "Payments processed"
          : "Unable to process payments"),
      type:
        action.notificationType === "success"
          ? NotificationType.Success
          : action.notificationType === "warning"
          ? NotificationType.Warning
          : NotificationType.Failure,
    });

    if (action.status === "success" && action.shouldRefresh) {
      methods.reset();
      onNavigate();
    }
  };

  const onSubmit = (formData: PaymentFormData) =>
    runPayGuarded((nextKey) => submitPayments(formData, nextKey));

  if (isLoading) {
    return (
      <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  return (
    <div className="payment-container d-flex flex-column gap-1 full-page-content">
      <PayableCard
        id="PayableDetailCard"
        className="payable-detail-header"
        isFooter={true}
        onRefresh={refreshAfterDelete}
        onDeleteSuccess={handleDeleteSuccess}
        dataHookState={{
          rows: data.paymentDetail ? [data.paymentDetail] : [],
          loading: isLoading,
        }}
        isEntity={isEntity}
          entityId={entityId}
          entityTypeId={entityTypeId}
      />
      {
        data.paymentDetail && (
          <>
            <Card className="no-bg p-0">
              <div className="row align-items-center mx-3 my-1">
                <div className="col-md-6">
                  <PaymentPayablesORReceivablesList
                    paymentMode={"Payables"}
                    payments={data.paymentPayeeList}
                    selectedPayments={paymentState.selectedPayments}
                    setSelectedPayments={handleSetSelectedPayments}
                    setDateFilterType={setDateFilterType}
                    setFullyQualifiedFilter={setFullyQualifiedFilter}
                    DATE_FILTERS={DATE_FILTERS}
                  />
                </div>
                <div className="col-md-6">
                  <div className="d-flex flex-column gap-3">
                    <FormProvider {...methods}>
                      <PaymentSummery
                        totalAmount={String(paymentState.totalAmount)}
                        paymentMethods={data.paymentMethod}
                        Payments={paymentState.selectedPayments}
                        showPlaid={showPlaid}
                      />
                    </FormProvider>
                  </div>
                </div>
              </div>
            </Card>

            <FixedFooter
              onSubmit={methods.handleSubmit(onSubmit)}
              loading={methods.formState.isSubmitting || isPaySubmitting}
              buttonLabel={submitButtonLabel}
              isSubmitDisabled={
                methods.formState.isSubmitting ||
                isPaySubmitting ||
                !methods.formState.isDirty ||
                !methods.formState.isValid
              }
              onCancel={onNavigate}
            />
          </>
        )
      }
    </div>
  );
};

export default PayablesPostPayment;